import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../../../lib/contexts/UserContext";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import { usePatientBooking } from "../../../lib/contexts/PatientBookingContext";
import { getSettingsByHospitalId } from "../../../lib/apis/adminpanel";
import {
  AllPatient,
  Patient,
  TableProps,
  TableUtilsContextType,
} from "./types";
import { formatPhoneNumber } from "./common/utils";
import Loader from "../../molecules/Loader";
import "../../../assets/styles/table.css";
import { getCookie } from "../../../lib/utils/cookies";
import { getCountryNumberLength } from "../../../lib/utils/funcs";
import { deleteRelation } from "../../../lib/apis/patient";
import { toast } from "react-toastify";
import {
  getReportDeleteUrl,
  getReportViewUrl,
} from "../../../lib/apis/booking";

const tableComponents: {
  [key: string]: React.LazyExoticComponent<React.ComponentType<any>>;
} = {
  doclist: React.lazy(() => import("./doctors/DoctorList")),
  doctors: React.lazy(() => import("./doctors/DoctorsTable")),
  dashboard_appointments: React.lazy(
    () => import("./appointments/DashboardAppointments")
  ),
  appointments: React.lazy(() => import("./appointments/AppointmentsTable")),
  all_appointments: React.lazy(() => import("./appointments/AllAppointments")),
  all_cancel_appointments: React.lazy(
    () => import("./appointments/CancelledAppointments")
  ),
  all_pending_appointments: React.lazy(
    () => import("./appointments/PendingAppointments")
  ),
  patients: React.lazy(() => import("./patients/PatientsTable")),
  all_patients: React.lazy(() => import("./patients/AllPatientsTable")),
  relations: React.lazy(() => import("./patients/RelationsTable")),
  reports: React.lazy(() => import("./reports/ReportsTable")),
  in_clinic: React.lazy(() => import("./appointments/InClinicAppointments")),
  scan: React.lazy(() => import("./appointments/ScanAppointments")),
  history: React.lazy(() => import("./appointments/HistoryAppointment")),
  transferred_appointment: React.lazy(
    () => import("./appointments/TransferredAppointment")
  ),

  booked: React.lazy(() => import("./appointments/BookedAppointments")),
  video_consultation: React.lazy(
    () => import("./appointments/VideoConsultations")
  ),
  followup: React.lazy(() => import("./appointments/FollowupAppointments")),
  completed: React.lazy(() => import("./appointments/CompletedAppointments")),
  cancelled: React.lazy(() => import("./appointments/CancelledAppointments")),
  create_prescription: React.lazy(
    () => import("./prescriptions/CreatePrescription")
  ),
  pending_in_clinic: React.lazy(
    () => import("./appointments/PendingInClinicAppointments")
  ),
};

// Create context for shared table utilities
export const TableUtilsContext = createContext<
  TableUtilsContextType | undefined
>(undefined);

export const useTableUtils = () => {
  const context = useContext(TableUtilsContext);
  if (!context) {
    throw new Error("useTableUtils must be used within a TableUtilsProvider");
  }
  return context;
};

const Table: React.FC<TableProps> = (props) => {
  const { type, isLoading = false } = props;
  const navigate = useNavigate();
  const { userData } = useUserData();
  const { hospitalID } = useHospDocData();
  const {
    setBookings,
    setAppointmentsData,
    SelectedDate,
    setPatient,
    setRelation,
  } = usePatientBooking();

  const [showNext, setShowNext] = useState<Record<number, boolean>>({});
  const [cancelReasonRequired, setCancelReasonRequired] = useState(false);

  useEffect(() => {
    const getSettings = async () => {
      const response = await getSettingsByHospitalId(hospitalID || "");
      if (response.status === 200) {
        setCancelReasonRequired(response.data.result[0].cancel_reason_needed);
      }
    };
    getSettings();
  }, [hospitalID]);

  const toggleShowNext = (index: number) => {
    setShowNext((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleAddAppointment = function (patient: AllPatient | Patient) {
    const countryCode = getCookie("countryCode") || "91";
    const numberLength = getCountryNumberLength(countryCode);
    let number = "";

    // Check if the patient is of type AllPatient or Patient
    if ("patient_contact_number" in patient && patient.patient_contact_number) {
      number =
        patient.patient_contact_number.length > numberLength
          ? patient.patient_contact_number.slice(-numberLength)
          : patient.patient_contact_number;
    } else if ("contact_number" in patient && patient.contact_number) {
      number =
        patient.contact_number.length > numberLength
          ? patient.contact_number.slice(-numberLength)
          : patient.contact_number;
    }

    // Navigate to the appointment booking page with the extracted number
    navigate("/" + hospitalID + "/appointments/book-an-appointment", {
      state: {
        number: number,
      },
    });
  };

  const handleDeleteRelation = async (relationId: string) => {
    // Add logic to delete relation
    const res = await deleteRelation(relationId);
    console.log(res);
    if (res?.status === 200) {
      //toast.success("Deleted successfully!");
    }
  };

  const handleReportView = async (booking_id: string, patient_id: string) => {
    try {
      if (!booking_id || !patient_id) {
        toast.error("Invalid booking ID or patient ID.");
      }

      const viewUrlResponse = await getReportViewUrl(booking_id, patient_id);
      if (viewUrlResponse.status !== 200) {
        toast.error("Failed to retrieve file.");
      }
      const viewUrl = viewUrlResponse.data.url;
      window.open(viewUrl, "_blank");
    } catch (error) {
      console.error("Error retrieving file:", error);
      alert("Failed to retrieve file.");
    }
  };

  const handleReportDelete = async (booking_id: string, patient_id: string) => {
    try {
      if (!booking_id || !patient_id) {
        toast.error("Invalid booking ID or patient ID.");
        return;
      }

      const deleteUrlResponse = await getReportDeleteUrl(
        booking_id,
        patient_id
      );
      if (deleteUrlResponse.status !== 200) {
        toast.error("Failed to retrieve delete URL.");
        return;
      }

      const deleteUrl = deleteUrlResponse.data.url;
      const deleteResponse = await fetch(deleteUrl, { method: "DELETE" });

      if (!deleteResponse.ok) {
        toast.error("Failed to delete file.");
        return;
      }

      toast.success("File deleted successfully.");
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file.");
    }
  };

  // Context value for shared utilities
  const contextValue = {
    showNext,
    toggleShowNext,
    cancelReasonRequired,
    handleAddAppointment,
    handleDeleteRelation,
    handleReportView,
    handleReportDelete,
  };

  if (isLoading) {
    return <Loader hospitalId={hospitalID} />;
  }

  const TableComponent = tableComponents[type.toLowerCase()];

  if (!TableComponent) {
    throw new Error(`Unknown table type: ${type}`);
  }

  return (
    <React.Suspense fallback={<Loader hospitalId={hospitalID} />}>
      <TableUtilsContext.Provider value={contextValue}>
        <div className="table-container overflow-x-auto side_bar ">
          <table className="w-full ">
            <TableComponent {...props} />
          </table>
        </div>
      </TableUtilsContext.Provider>
    </React.Suspense>
  );
};

export default Table;
